import React, { useState, useEffect, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { CommonTable } from '../../components/CommonTable';
import { Button } from '../../components/ui/Button';
import { useAppSelector, useAppDispatch } from "../../store/hooks/reduxHooks";
import {
  ExternalApiLog,
  LogFilters,
  fetchExternalLogsThunk,
  setPage,
  setPageSize,
  setFilters,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  setHasFetched
} from "../../features/externalLogs/externalLogsSlice";
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Globe,
  ShoppingBag,
  Server,
  Phone
} from 'lucide-react';
import { validateCustomerSearchInput } from "../../utils/searchValidator";

const ExternalAPILogsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    total,
    loading,
    page,
    pageSize,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    hasFetched,
    logs
  } = useAppSelector((state) => state.externalLogs);
  const { token } = useAppSelector((state) => state.auth);
  const [showFilters, setShowFilters] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchValid, setIsSearchValid] = useState(false);
  const handleSearchInputChange = (value: string) => {
    setLocalSearch(value);

    // ✅ When input is cleared
    if (!value.trim()) {
      setSearchError(null);
      setIsSearchValid(true); // allow reset

      // 🔁 Reset table immediately
      dispatch(setPage(1));
      dispatch(setSearchQuery("")); // empty search
      return;
    }

    // Normal validation
    const result = validateCustomerSearchInput(value);
    setIsSearchValid(result.isValid);
    setSearchError(result.message || null);
  };


  const handleSearchSubmit = () => {
    // If empty, already handled in change
    if (!localSearch.trim()) return;

    if (!isSearchValid) return;

    dispatch(setPage(1));
    dispatch(setSearchQuery(localSearch));
  };

  const handleResetSearch = () => {
    setLocalSearch("");    // clears input
    setSearchError(null);  // clears error
    setIsSearchValid(false); // disables search button
  };


  const prevRef = useRef({
    page,
    pageSize,
    sortBy,
    sortOrder,
    searchQuery,
    filters
  });


  // ✅ Fetch schemes only once when tab first opens
  useEffect(() => {
    if (!hasFetched) return;

    const prev = prevRef.current;

    const hasChanged =
      prev.searchQuery !== searchQuery ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.sortBy !== sortBy ||
      prev.sortOrder !== sortOrder ||
      prev.filters.platform !== filters.platform ||
      prev.filters.apiProvider !== filters.apiProvider;

    if (!hasChanged) return;

    const timeout = setTimeout(() => {
      if (!token) return;
      dispatch(fetchExternalLogsThunk(token));

      // 🔑 update snapshot AFTER dispatch
      prevRef.current = {
        searchQuery,
        filters: {
          ...filters,
        },
        page,
        pageSize,
        sortBy,
        sortOrder,
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    searchQuery,
    filters.platform,
    filters.apiProvider,
    page,
    pageSize,
    sortBy,
    sortOrder,
    hasFetched,
    dispatch,
  ]);



  useEffect(() => {
    if (!token) return;
    if (hasFetched) return;


    dispatch(fetchExternalLogsThunk(token));

  }, [token, hasFetched, dispatch]);

  const handleRefresh = () => {
    if (!token) return;
    dispatch(setPage(1));
    dispatch(fetchExternalLogsThunk(token));
  };


  const handleFilterChange = (key: keyof LogFilters, value: any) => {
    dispatch(setPage(1));
    dispatch(
      setFilters({
        ...filters,
        [key]: value || undefined,
      })
    );
  };


  const handleResetFilters = () => {
    if (!token) return;

    dispatch(
      setFilters({
        apiProvider: '',
        platform: '',
        from: '',
        to: '',
      })
    );

    dispatch(setPage(1)); // reset page
    dispatch(fetchExternalLogsThunk(token)); // trigger API immediately
  };



  // Badge renderers (same as before)
  const renderApiProviderBadge = (provider: string) => {
    let bg = '', text = 'text-white';
    switch (provider.toLowerCase()) {
      case 'zype': bg = 'bg-black'; break;
      case 'cashe': bg = 'bg-yellow-500'; break;
      case 'easebuzz': bg = 'bg-blue-500'; break;
      default: bg = 'bg-gray-300'; text = 'text-gray-800';
    }
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>{provider}</span>;
  };

  const renderPlatformBadge = (platform: string) => {
    let IconComponent = Server;
    let color = 'text-gray-500';
    switch (platform.toLowerCase()) {
      case 'merchant': IconComponent = ShoppingBag; color = 'text-blue-500'; break;
      case 'gateway': IconComponent = Globe; color = 'text-green-500'; break;
    }
    return (
      <div className="flex items-center space-x-1">
        <IconComponent size={16} className={color} />
        <span className="text-sm">{platform}</span>
      </div>
    );
  };

  const renderStatusBadge = (statusCode: number, message?: string) => {
    let bg = '', text = 'text-white', icon = null;
    if (statusCode >= 200 && statusCode < 300) { bg = 'bg-green-100'; text = 'text-green-800'; icon = <CheckCircle size={14} className="mr-1" />; }
    else if (statusCode >= 400 && statusCode < 500) { bg = 'bg-red-100'; text = 'text-red-800'; icon = <XCircle size={14} className="mr-1" />; }
    else if (statusCode >= 500) { bg = 'bg-yellow-100'; text = 'text-yellow-800'; icon = <AlertTriangle size={14} className="mr-1" />; }
    else { bg = 'bg-gray-100'; text = 'text-gray-800'; icon = <Server size={14} className="mr-1" />; }
    return (
      <div className="flex flex-col">
        <span className={`flex items-center w-fit px-2 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
          {icon} {statusCode}
        </span>
        {statusCode === 400 && message && <span className="text-[11px] text-gray-600 mt-1 break-words max-w-[200px] leading-tight">{message}</span>}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';

      hours = hours % 12;
      hours = hours ? hours : 12; // 0 → 12
      const formattedHours = String(hours).padStart(2, '0');

      return `${day}/${month}/${year}, ${formattedHours}:${minutes} ${ampm}`;
    } catch {
      return dateString;
    }
  };


  const columns: ColumnDef<ExternalApiLog>[] = [
    {
      id: 'serialNo',
      header: 'Sr. No',
      cell: ({ row }) => ((page - 1) * pageSize + row.index + 1)
    },

    { id: 'apiProvider', header: 'API Provider', cell: ({ row }) => renderApiProviderBadge(row.original.apiProvider), size: 120 },
    { id: 'platform', header: 'Platform', cell: ({ row }) => renderPlatformBadge(row.original.platform), size: 120 },
    { id: 'statusCode', header: 'Status Code', cell: ({ row }) => renderStatusBadge(row.original.statusCode, row.original.responseBody?.message), size: 120 },
    { id: 'apiName', header: 'API Name', accessorKey: 'apiName', cell: ({ row }) => <span className="text-sm">{row.original.apiName}</span>, size: 200 },
    {
      id: 'user', header: 'User', accessorKey: 'user', cell: ({ row }) => (
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <Phone size={14} className="text-brand-blue" />
          <span>{row.original.user || 'N/A'}</span>
        </div>
      ), size: 150
    },
    { id: 'createdAt', header: 'Timestamp', accessorKey: 'createdAt', cell: ({ row }) => <span className="text-sm">{formatDate(row.original.createdAt)}</span>, size: 160 },
  ];

  return (
    <div className="w-full">
      <div className="flex justify-between mb-4">
        <Button variant="outline" onClick={() => setShowFilters(prev => !prev)} size="sm">Filters</Button>
      </div>

      {showFilters && (
        <div className="p-4 mb-4 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-wrap gap-4">
          {/* API Provider */}
          <div className="flex flex-col w-52">
            <label className="mb-1 text-sm font-medium text-gray-700">API Provider</label>
            <select className="h-9 px-2 border border-gray-300 rounded-md" value={filters.apiProvider || ''} onChange={e => handleFilterChange('apiProvider', e.target.value)}>
              <option value="">All Providers</option>
              <option value="zype">Zype</option>
              <option value="cashe">Cashe</option>
              <option value="easeBuzz">EaseBuzz</option>
            </select>
          </div>

          {/* Platform */}
          <div className="flex flex-col w-52">
            <label className="mb-1 text-sm font-medium text-gray-700">Platform</label>
            <select className="h-9 px-2 border border-gray-300 rounded-md" value={filters.platform || ''} onChange={e => handleFilterChange('platform', e.target.value)}>
              <option value="">All Platforms</option>
              <option value="merchant">Merchant</option>
              <option value="gateway">Gateway</option>
            </select>
          </div>

          {/* From */}
          <div className="flex flex-col w-52">
            <label className="mb-1 text-sm font-medium text-gray-700">From</label>
            <input type="date" className="h-9 px-2 border border-gray-300 rounded-md" value={filters.from || ''} onChange={e => handleFilterChange('from', e.target.value)} />
          </div>

          {/* To */}
          <div className="flex flex-col w-52">
            <label className="mb-1 text-sm font-medium text-gray-700">To</label>
            <input type="date" className="h-9 px-2 border border-gray-300 rounded-md" value={filters.to || ''} onChange={e => handleFilterChange('to', e.target.value)} />
          </div>

          {/* Buttons */}
          <div className="flex justify-end w-full mt-4 gap-2">
            <Button variant="outline" onClick={handleResetFilters} size="sm" className="px-3 py-1 text-sm">Reset</Button>
            <Button
              variant="default"
              size="sm"
              className="px-3 py-1 text-sm"
              onClick={() => {
                dispatch(setPage(1));
                dispatch(setHasFetched(false)); // triggers useEffect fetch
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      )}

      <CommonTable
        columns={columns}
        data={logs}
        isLoading={loading}

        page={page}
        pageSize={pageSize}
        total={total}

        onPageChange={(p) => dispatch(setPage(p))}
        onPageSizeChange={(size) => {
          dispatch(setPage(1));          // 🔑 ADD THIS
          dispatch(setPageSize(size));
        }}
        onSortChange={(field, order) => {
          dispatch(setPage(1));          // 🔑 ADD THIS
          dispatch(setSortBy(field));
          dispatch(setSortOrder(order));
        }}

        searchValue={localSearch}
        onSearchChange={handleSearchInputChange}
        onSearchSubmit={handleSearchSubmit}
        isSearchDisabled={!isSearchValid}
        searchError={searchError}
        onResetSearch={handleResetSearch} // ✅ new
        onRefresh={handleRefresh}

        searchPlaceholder="Search API name, method..."
      />

    </div>
  );
};

export default ExternalAPILogsPage;
